import { Grid, IconButton, useEventCallback } from '@mui/material';
import { Fragment, memo, useMemo } from 'react';
import { PersonVM, RegistrationBewareFormVM } from 'mutation/view-models/registration-beware';
import { headCells } from 'shell/constants/person-list';
import Table from 'shell/components/shared/table';
import { noop } from 'shell/utils/noop';
import { AnprVM, SearchPerson } from 'shell/view-models/search-anpr';
import { Edit } from '@mui/icons-material';
import { PersonVM as PersonListVM } from 'shell/view-models/search-person';
import { useFormikContext } from 'formik';
import { transformFamilyCoexistence } from 'shell/utils/consult-person/response-transformer/family/family-coexistence';
import { transformCitizenship } from 'shell/utils/consult-person/response-transformer/registry-office/citizenship';
import { transformPerson } from 'shell/utils/consult-person/response-transformer/person/person';
import { useBus } from 'shell/hooks/use-bus';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { HttpResponse } from 'shell/models/http-response';
import { SubjectsList } from 'shell/models/person';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';

interface TablePersonsProps {
  values?: PersonVM | null;
  onChange: (key: keyof PersonVM, value: PersonVM[keyof PersonVM]) => void;
  onChanges: (value: PersonVM) => void;
  isBeware?: boolean;
}

const TablePersonsComponent = ({
  values,
  isBeware,
  onChange,
  onChanges,
}: TablePersonsProps): React.ReactElement => {
  const formikContext = useFormikContext<RegistrationBewareFormVM>();
  const dispatch = useBus();

  const onChangePage = useEventCallback((page: number) => {
    onChange('page', page || 0);
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    const newDataset: PersonVM = {
      ...(values || {}),
      rowsPerPage: rowsPerPage || 0,
      page: 0,
    };

    onChanges(newDataset);
  });

  const getPerson = useHttpClient<HttpResponse<SubjectsList>>(
    `${__PERSON_API_URL__}/persona/registrazione-diffida/ricerca-persona`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
    }
  );

  const onEditPress = useEventCallback((person: PersonListVM) => {
    const data: SearchPerson = {
      datiRichiesta: isBeware
        ? {
            ricercaNazionale: '0',
          }
        : undefined,
      criteriRicerca: {
        codiceFiscale: person?.fiscalCode || undefined,
        idSchedaSoggettoANPR: person.generality?.idSchedaSoggettoANPR || undefined,
      },
    };
    (getPerson({ data }) as ResponsePromise<HttpResponse<SubjectsList>>).then((response) => {
      const castedResponse = response?.data.payload.datiSoggetto[0] || {};
      if (castedResponse) {
        const newDataset = transformPerson(castedResponse) || {};
        const datasetCasted = newDataset as PersonVM;
        datasetCasted.codicePersonaSipo = castedResponse?.codicePersonaSipo;

        const familyCoexistence = transformFamilyCoexistence(castedResponse);
        const citizenship = transformCitizenship(castedResponse);

        formikContext.setValues({
          ...formikContext.values,
          searchPerson: null,
          person: datasetCasted,
          familyCoexistence,
          subjectAttributes: {
            ...formikContext.values.subjectAttributes,
            disabled: false,
          },
          citizenship,
        });
        dispatch.emit('CLOSE_SEARCH');
      }
    });
  });

  const body: Array<PersonListVM> = useMemo(() => {
    const formattedBody: Array<PersonListVM> = (
      ((values?.personsList as AnprVM<PersonListVM>)?.persons as Array<PersonListVM>) || []
    ).map((person) => {
      return {
        ...person,
        action: (
          <Fragment>
            <IconButton
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onEditPress(person);
              }}
              aria-label="Visualizza elemento"
              aria-haspopup="true"
              style={{
                padding: '4px',
              }}
            >
              <Edit />
            </IconButton>
          </Fragment>
        ),
      };
    });

    return formattedBody;
  }, [values?.personsList, onEditPress]);

  return (
    <Grid item xs={12}>
      <Table
        ariaLabel="Lista Persone"
        page={values?.page || 0}
        rowsPerPage={values?.rowsPerPage}
        body={body || []}
        count={body?.length || 0}
        head={headCells}
        onSort={noop}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
        orders={{}}
        isNotLayoutFixed
        noSort
        hideButtons
      />
    </Grid>
  );
};

TablePersonsComponent.displayName = 'Accordion person form component';

const TablePersons = memo(TablePersonsComponent);

export default TablePersons;
